<script setup lang="ts">
  import {ref, onBeforeUnmount} from 'vue';
  import {useI18n} from 'vue-i18n';
  import {DateTime} from 'luxon';
  import useCpBem from '~/composables/useCpBem';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import type {ICpArticle} from '~/types';

  defineProps<{
    product: ICpArticle
  }>();
  const {b, e, em} = useCpBem('cp-section-pdp-featured-product');
  const {t} = useI18n();
  const day = ref(DateTime.now().toFormat('dd/LL/y'));
  // TODO Add CpCountdown

  const interval = ref<string>();
  const countdownInterval = ref<any>();

  const timer = () => {
    countdownInterval.value = setInterval(() => {
      const currentTime = DateTime.now().endOf('day');
      const diffTime = currentTime.diff(DateTime.now(), ['hours', 'minutes', 'seconds']);
      const hours = diffTime.hours.toFixed(0).padStart(2, '0');
      const minutes = diffTime.minutes.toFixed(0).padStart(2, '0');
      const seconds = diffTime.seconds.toFixed(0).padStart(2, '0');
      interval.value = `${hours}h ${minutes}m ${seconds}s`;
    }, 1000);
  };

  onMounted(() => {
    timer();
  });

  onBeforeUnmount(() => {
    clearInterval(countdownInterval.value);
  });

</script>

<template>
    <div :class="b">
        <div :class="e('animationBox')">
            <div :class="e('animationContainer')">
                <CpFeaturedAnimation />
            </div>
            <div :class="e('discount')">
                <CpText variant="subheading" type="mkt-dark" :class="em('leftTxt',{'pb': product.isOffer})">
                    {{ !product.offer.percent ? t('todayFeatured') : `-${product.offer.percent }%` }}
                </CpText>
                <CpText v-if="product.offer.absolute" variant="subheading" type="mkt-dark">
                    ¡${{ product.offer.absolute }} {{ t('less') }}!
                </CpText>
            </div>
        </div>
        <div :class="e('countBox')">
            <CpText variant="heading-h3-fake" type="mkt-dark" :class="e('rightTimer')">
                {{ interval }}
            </CpText>
            <CpText variant="caption-regular" type="neutral">
                {{ t('validUntil') }}:
                <CpText variant="caption-bold" type="neutral">
                    {{ day }}
                </CpText>
            </CpText>
        </div>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global";
  .cp-section-pdp-featured-product {
    display: flex;
    align-content: center;
    padding: $cp-gutter;
    border-top: $cp-border-standard;
    border-bottom: $cp-border-standard;

    &__count-box {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      align-items: flex-end;
      justify-content: flex-end;
      height: 36px;
    }
    &__animation-box{
      display: flex;
      align-items: center;
    }
    &__animation-container{
      width: 24px;
      height: 24px;
      margin-right: $cp-gutter-small;
    }
    &__right-timer {
      padding-bottom: $cp-gutter-xsmall;
    }
    &__discount {
      display: flex;
      flex-direction: column;
    }
    &__left-txt {
      &--pb {
        padding-bottom: $cp-gutter-xsmall;
      }
    }
  }
</style>

<i18n locale="es-US">
  validUntil: 'Válido hasta'
  todayFeatured: '!Destacados del día!'
  less: 'menos'
</i18n>
